import { useState } from 'react';
import { practiceAreasContent } from '@/mocks/homeData';
import { useScrollReveal } from '@/hooks/useScrollReveal';

type TabKey = 'service' | 'process' | 'faq';

export default function PracticeSection() {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('service');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollReveal(0.1);
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollReveal(0.1);

  const selectedArea = selectedIndex !== null ? practiceAreasContent.areas[selectedIndex] : null;

  const openModal = (index: number) => {
    setSelectedIndex(index);
    setActiveTab('service');
    setOpenFaqIndex(null);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedIndex(null);
    document.body.style.overflow = '';
  };

  const handleCtaClick = () => {
    closeModal();
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }, 300);
  };

  return (
    <section
      id="practice"
      className="relative py-20 md:py-28 bg-background-100"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 xl:px-16">
        <div className="max-w-6xl mx-auto">
          <div
            ref={headerRef}
            className={`text-center mb-12 md:mb-16 transition-all duration-700 ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="inline-block px-4 py-1.5 rounded-full text-xs md:text-sm font-medium tracking-wider bg-accent-100 text-accent-700 mb-4">
              Practice Areas
            </span>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 tracking-tight">
              {practiceAreasContent.sectionTitle}
            </h2>
            <p className="mt-3 text-base md:text-lg text-foreground-600 max-w-2xl mx-auto">
              {practiceAreasContent.sectionSubtitle}
            </p>
          </div>

          <div
            ref={cardsRef}
            className={`grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6 max-w-4xl mx-auto transition-all duration-700 delay-200 ${
              cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {practiceAreasContent.areas.map((area, index) => (
              <div
                key={area.title}
                className="group relative p-6 md:p-8 rounded-2xl bg-background-50 border border-background-200/70 hover:border-primary-300/60 hover:shadow-lg transition-all duration-300 cursor-pointer"
                style={{ transitionDelay: `${index * 100}ms` }}
                onClick={() => openModal(index)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-300">
                    <span className="w-6 h-6 md:w-7 md:h-7 flex items-center justify-center">
                      <i className={`${area.icon} text-xl md:text-2xl`} />
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg md:text-xl font-bold text-foreground-950">
                      {area.title}
                    </h3>
                    <p className="mt-1 text-sm text-foreground-500 leading-relaxed">
                      {area.description}
                    </p>
                  </div>
                </div>
                <div className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-background-200/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-up-line text-sm text-foreground-600" />
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div
            className={`mt-10 md:mt-14 text-center transition-all duration-700 delay-300 ${
              cardsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <div className="inline-flex items-center gap-3 md:gap-4 px-5 md:px-7 py-3 md:py-4 rounded-2xl bg-background-50 border border-accent-200/70 shadow-sm">
              <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 shrink-0">
                <span className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                  <i className="ri-check-double-line text-lg md:text-xl" />
                </span>
              </div>
              <p className="text-sm md:text-base font-semibold text-foreground-800 leading-relaxed text-left max-w-lg">
                {practiceAreasContent.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {selectedArea && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
          onClick={closeModal}
        >
          <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm animate-fade-in" />

          <div
            className="relative w-full max-w-xl bg-background-50 rounded-2xl shadow-2xl animate-scale-in overflow-hidden flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with image */}
            <div className="relative h-40 md:h-48 shrink-0 overflow-hidden">
              <img
                src={selectedArea.image}
                alt={selectedArea.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-background-50/20 backdrop-blur-sm text-background-50">
                    <span className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center">
                      <i className={`${selectedArea.icon} text-lg md:text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-background-50">
                    {selectedArea.title}
                  </h3>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-foreground-950/30 backdrop-blur-sm text-background-50 hover:bg-foreground-950/50 transition-colors cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-close-line text-lg" />
                </span>
              </button>
            </div>

            {/* Tab bar */}
            <div className="flex items-center gap-1 px-6 pt-5 pb-1 shrink-0">
              {[
                { key: 'service' as TabKey, label: '서비스 안내', icon: 'ri-information-line' },
                { key: 'process' as TabKey, label: '진행 절차', icon: 'ri-list-ordered' },
                { key: 'faq' as TabKey, label: '자주 묻는 질문', icon: 'ri-question-answer-line' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setOpenFaqIndex(null);
                  }}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    activeTab === tab.key
                      ? 'bg-primary-500 text-background-50 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-800 hover:bg-background-200/60'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${tab.icon} text-sm`} />
                  </span>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="overflow-y-auto px-6 py-5 flex-1">
              {/* Service tab */}
              {activeTab === 'service' && (
                <div className="animate-tab-in">
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                    {selectedArea.description}
                  </p>
                  <h4 className="flex items-center gap-2 text-sm font-semibold text-foreground-800 mb-3">
                    <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                      <i className="ri-check-double-line" />
                    </span>
                    주요 서비스 내용
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedArea.items.map((item) => (
                      <div
                        key={item.text}
                        className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/60 hover:border-primary-200 hover:bg-background-50 transition-all duration-200 cursor-default"
                      >
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className={`${item.icon} text-sm`} />
                          </span>
                        </div>
                        <span className="text-sm md:text-base font-medium text-foreground-800">
                          {item.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Process tab */}
              {activeTab === 'process' && (
                <div className="animate-tab-in">
                  <div className="space-y-0">
                    {selectedArea.process.map((step, idx) => (
                      <div
                        key={step.step}
                        className="flex gap-4"
                      >
                        <div className="flex flex-col items-center">
                          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-sm font-bold shrink-0">
                            {step.step}
                          </div>
                          {idx < selectedArea.process.length - 1 && (
                            <div className="w-0.5 flex-1 bg-primary-200 my-1" />
                          )}
                        </div>
                        <div className={`pb-6 ${idx === selectedArea.process.length - 1 ? '' : ''}`}>
                          <h5 className="text-sm font-bold text-foreground-900 mb-1">
                            {step.title}
                          </h5>
                          <p className="text-sm text-foreground-500 leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQ tab */}
              {activeTab === 'faq' && (
                <div className="animate-tab-in space-y-2">
                  {selectedArea.faq.map((item, idx) => (
                    <div
                      key={idx}
                      className="rounded-xl border border-background-200/60 overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenFaqIndex(openFaqIndex === idx ? null : idx)}
                        className="w-full flex items-center justify-between p-4 text-left hover:bg-background-100/70 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3 pr-3">
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 shrink-0">
                            <span className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-question-line text-sm" />
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-foreground-800">
                            {item.question}
                          </span>
                        </div>
                        <span className={`w-5 h-5 flex items-center justify-center shrink-0 transition-transform duration-200 ${openFaqIndex === idx ? 'rotate-180' : ''}`}>
                          <i className="ri-arrow-down-s-line text-foreground-400" />
                        </span>
                      </button>
                      <div
                        className={`overflow-hidden transition-all duration-300 ${
                          openFaqIndex === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <p className="px-4 pb-4 pl-[60px] text-sm text-foreground-500 leading-relaxed">
                          {item.answer}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* CTA button */}
            <div className="px-6 py-4 border-t border-background-200/70 shrink-0">
              <button
                onClick={handleCtaClick}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-primary-500 text-background-50 font-semibold text-sm md:text-base hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-line" />
                </span>
                상담 문의하기
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes tab-in {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.2s ease-out; }
        .animate-scale-in { animation: scale-in 0.25s ease-out; }
        .animate-tab-in { animation: tab-in 0.2s ease-out; }
      `}</style>
    </section>
  );
}